import { getToken } from '@/core/token';

/**
 * Value for the `Authorization` header on every API call.
 *
 * The API Gateway authorizer caches its result keyed on this header, so it
 * must (a) always be present when a credential exists — the JWT is sent as a
 * Bearer header even where the cookie would also be sent — and (b) uniquely
 * identify the caller: for share links the per-browser session id is folded
 * into the token (`Token <token>:<base64 password>:<sessionId>`) so two
 * viewers of the same link never share a cached authorizer context.
 */
export const getAuthToken = () => {
  // A share link (`?token=`) must win over a logged-in session: the shared
  // views only work with the link's authorizer context (projectId, linkId),
  // and a stale cookie JWT would otherwise be sent as Bearer and denied.
  const token = new URLSearchParams(window.location.search).get('token');
  if (token) {
    const linkPassword = sessionStorage.getItem('link-password');
    const password = linkPassword ? btoa(linkPassword) : '';
    return `Token ${token}:${password}:${getLinkSessionId() ?? ''}`;
  }

  const jwt = getToken();
  if (jwt) return `Bearer ${jwt}`;

  return '';
};

export const getLinkSessionId = (): string | null => {
  const token = new URLSearchParams(window.location.search).get('token');
  if (!token) return null;

  // Per-token session id, stored in localStorage so it survives a tab close —
  // ephemeral quick-upload layers belong to this id and need to outlive page reloads.
  const key = `link-session-id:${token}`;
  let sessionId = localStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(key, sessionId);
  }
  return sessionId;
};
